import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductInformation from 'components/commercetools-ui/organisms/product/product-details/components/product-information';
import useTrack from 'components/commercetools-ui/organisms/product/product-details/hooks/useTrack';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { useCart, useProduct } from 'frontastic';
import { PDP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import ProductDetailsGallery from '../gallery';
import Upsell from '../upsell';

export interface ProductDetailsCoursesSellableProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  isTurnOnGallery?: boolean;
  isLazyLoad?: boolean;
}

const ProductDetailsCoursesSellable: FC<ProductDetailsCoursesSellableProps> = ({
  product,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  isTurnOnGallery = true,
  isLazyLoad = true,
}) => {
  const { addItem } = useCart();
  const { formatMessage } = useFormat({ name: 'cart' });

  const [isDesktopSize] = useMediaQuery(desktop);
  const [category, setCategory] = useState<Category>();
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  const { categories } = useProduct();

  useEffect(() => {
    if (product && categories && !category) {
      const currentCategory = categories.find((cat) => cat.categoryId === product.categories?.[0]?.categoryId);
      setCategory(currentCategory);
    }
  }, [categories, category, product, product?.categories]);

  const { trackAddToCart } = useTrack({ product, inModalVersion });

  const { show, fetchRelatedProducts } = useAddToCartOverlay();

  const handleQuantityChange = (e: React.FormEvent) => {
    setQuantity(+(e.target as HTMLSelectElement).value);
  };

  const showMoreInfo = useMemo(() => {
    return product?.variants[0].attributes?.['section-13'];
  }, [product]);

  const handleAddToCart = async () => {
    setLoading(true);

    addItem(product, variant, quantity).then(() => {
      setLoading(false);

      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);

      show(product, variant, quantity);
      onAddToCart?.();
    });
    trackAddToCart(quantity);
  };

  useEffect(() => {
    fetchRelatedProducts(product);
  }, [product, fetchRelatedProducts]);

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 px-20 md:pr-36'
    : 'pt-20 md:pt-24 pb-32 md:grid md:grid-cols-12 md:items-start mx-auto max-w-7xl px-24 lg:px-32';

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-5 lg:col-span-4',
    'mt-24 md:mt-0 h-auto min-h-150',
  ]);

  return (
    <div className={wrapperClassName}>
      {category && isDesktopSize && !inModalVersion && (
        <Breadcrumb Separator="/" className="col-span-12 mb-24 w-fit">
          <Link key={category.categoryId} link={category._url} className="text-14 text-primary-black">
            {category.name}
          </Link>

          <Typography key={product.slug} className="cursor-default text-14 text-neutral-500">
            {product.name}
          </Typography>
        </Breadcrumb>
      )}
      <ProductDetailsGallery
        product={product}
        variant={variant}
        inModalVersion={inModalVersion}
        isTurnOnGallery={isTurnOnGallery}
        isLazyLoad={isLazyLoad}
      />
      <div className={informationContainerClassName}>
        <ProductInformation
          product={product}
          variant={variant}
          onChangeVariant={onChangeVariant}
          inModalVersion={inModalVersion}
        />

        <div className="flex gap-8 pt-20">
          <Dropdown
            className="h-full rounded-sm"
            value={quantity.toString()}
            items={Array(10)
              .fill(0)
              .map((_, index) => {
                const value = `${index + 1}`;
                return {
                  label: value,
                  value,
                };
              })}
            onChange={handleQuantityChange}
          />
          <Button
            className="w-full rounded-sm text-14 font-medium"
            variant="primary"
            onClick={handleAddToCart}
            loading={loading}
            added={added}
            dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
          >
            {formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
          </Button>
        </div>

        {showMoreInfo && <Upsell product={product} />}

        {/* {!inModalVersion && <ShippingSection />} */}

        {inModalVersion && (
          <Link
            link={url}
            className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
            onClick={() => setIsOpen?.(false)}
          >
            {formatMessage({ id: 'more.details', defaultMessage: 'More details' })}
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsCoursesSellable;
